import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signInAnonymously, signInWithCustomToken, signOut, deleteUser, updateProfile, linkWithCredential, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail, sendEmailVerification, verifyBeforeUpdateEmail, reload, onAuthStateChanged, } from "firebase/auth";
import { AppError } from "../../domain/models/appError";
import { mapFirebaseError } from "./errorMapper";
function toAuthUser(firebaseUser) {
    return {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        isAnonymous: firebaseUser.isAnonymous,
        isEmailVerified: firebaseUser.emailVerified,
        creationDate: firebaseUser.metadata.creationTime ? new Date(firebaseUser.metadata.creationTime) : null,
    };
}
export class FirebaseAuthRepository {
    auth = getAuth();
    get currentUser() {
        const user = this.auth.currentUser;
        return user ? toAuthUser(user) : null;
    }
    authStateStream() {
        return {
            subscribe: (cb) => {
                const unsubscribe = onAuthStateChanged(this.auth, (user) => {
                    cb(user ? toAuthUser(user) : null);
                });
                const current = this.auth.currentUser;
                if (current)
                    cb(toAuthUser(current));
                return unsubscribe;
            },
        };
    }
    async isAdmin() {
        if (!this.auth.currentUser)
            return false;
        try {
            const token = await this.auth.currentUser.getIdTokenResult();
            return token.claims.admin === true;
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async signIn(email, password) {
        try {
            const cred = await signInWithEmailAndPassword(this.auth, email, password);
            return toAuthUser(cred.user);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async signUp(email, password) {
        try {
            const cred = await createUserWithEmailAndPassword(this.auth, email, password);
            return toAuthUser(cred.user);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async signInAnonymously() {
        try {
            const cred = await signInAnonymously(this.auth);
            return toAuthUser(cred.user);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async signInWithCustomToken(token) {
        try {
            const cred = await signInWithCustomToken(this.auth, token);
            return toAuthUser(cred.user);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async signOut() {
        try {
            await signOut(this.auth);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async deleteCurrentUser() {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            await deleteUser(this.auth.currentUser);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async updateDisplayName(name) {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            await updateProfile(this.auth.currentUser, { displayName: name });
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async linkEmailPassword(email, password) {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            const credential = EmailAuthProvider.credential(email, password);
            await linkWithCredential(this.auth.currentUser, credential);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async changeEmail(newEmail) {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            await verifyBeforeUpdateEmail(this.auth.currentUser, newEmail);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async reauthenticate(password) {
        if (!this.auth.currentUser?.email)
            throw new AppError("notAuthenticated");
        try {
            const credential = EmailAuthProvider.credential(this.auth.currentUser.email, password);
            await reauthenticateWithCredential(this.auth.currentUser, credential);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async sendPasswordReset(email) {
        try {
            await sendPasswordResetEmail(this.auth, email);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async sendEmailVerification() {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            await sendEmailVerification(this.auth.currentUser);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async reloadCurrentUser() {
        if (!this.auth.currentUser)
            throw new AppError("notAuthenticated");
        try {
            await reload(this.auth.currentUser);
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
}
//# sourceMappingURL=FirebaseAuthRepository.js.map